import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  NoActiveDomainProductsModalDescription,
  NoActiveDomainProductsModalWrapper,
  NoActiveDomainProductsModalStyled,
  NoActiveDomainProductsModalOption,
  NoActiveDomainProductsModalSelectWrapper,
} from './NoActiveDomainProductsModal.styled'
import { useEditDomainProducts } from 'Features/domainsManagement/api/editDomainProducts'
import {
  Button,
  ButtonWithIcon,
  DesignedModal,
  GeneralSelect,
  Toggle,
  useTriggerOnPressEnter,
} from '@clickcease/cc-client-components-lib'
import { Diskette } from 'Assets/icons/Diskette'
import { useGetPackagingLimits } from 'Features/accountSettings/api/getPackagingLimits'
import { Domain } from 'Types/Domain'

type EditDomainProductsModalProps = {
  domains: Array<Domain>
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  onModalClose: (isClosed: boolean) => void
}

interface chosenDomainType {
  label: string
  value: string
  id: number
  botZapping: boolean
  paidMarketing: boolean
}

export const NoActiveDomainProductsModal = ({
  domains,
  isOpen,
  onOpenChange,
  onModalClose,
}: EditDomainProductsModalProps) => {
  const { t } = useTranslation('domainManagement')
  const [paidMarketing, setPaidMarketing] = useState<boolean>(false)
  const [botZapping, setBotZapping] = useState<boolean>(false)
  const { data: packagesData } = useGetPackagingLimits()
  const { mutate, isLoading } = useEditDomainProducts()
  const [chosenDomain, setChosenDomain] = useState<chosenDomainType>()

  const domainsOptions: chosenDomainType[] = domains.map((domain) => ({
    label: domain.address,
    value: domain.address,
    id: domain.id,
    botZapping: domain.botZapping,
    paidMarketing: domain.paidMarketing,
  }))

  const handleChosenDomain = (newValue: chosenDomainType | null) => {
    if (newValue) {
      setChosenDomain(newValue)
      setPaidMarketing(newValue.paidMarketing)
      setBotZapping(newValue.botZapping)
    }
  }

  const save = () => {
    const params = {
      domainId: chosenDomain?.id as number,
      products: {
        paidMarketing,
        botZapping,
      },
    }

    mutate(params, {
      onSuccess: () => {
        onOpenChange(false)
        onModalClose(true)
      },
    })
  }

  useTriggerOnPressEnter(save)

  useEffect(() => {
    if (domains?.length === 1) {
      setChosenDomain(domainsOptions[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <DesignedModal
      disableAutoFocus
      title={t('edit_domain_products.title')}
      isOpen={isOpen}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen)
        onModalClose(true)
      }}
      actions={
        <>
          <Button
            variant="ghost"
            text={t('edit_domain_products.cancel')}
            onClick={() => {
              onOpenChange(false)
              onModalClose(true)
            }}
          />
          {!!chosenDomain && (
            <ButtonWithIcon
              iconLeft={<Diskette stroke="#ffffff" fill="rgba(225, 36, 104, 1)" />}
              text={t('edit_domain_products.save')}
              loading={isLoading}
              onClick={() => save()}
            />
          )}
        </>
      }
    >
      <NoActiveDomainProductsModalStyled>
        <NoActiveDomainProductsModalWrapper>
          <NoActiveDomainProductsModalDescription>
            {domains?.length > 1
              ? t('no_active_domain_products.select_which')
              : t('edit_domain_products.select_which')}
          </NoActiveDomainProductsModalDescription>
          <NoActiveDomainProductsModalSelectWrapper>
            {domains?.length > 1 ? (
              <GeneralSelect
                options={domainsOptions}
                value={chosenDomain}
                onChange={handleChosenDomain}
              />
            ) : (
              <div>{domains[0]?.address}</div>
            )}
          </NoActiveDomainProductsModalSelectWrapper>
          <NoActiveDomainProductsModalOption>
            <Toggle
              value={paidMarketing}
              disabled={!packagesData?.paidMarketingAllowed || !chosenDomain}
              onChange={() => {
                setPaidMarketing(!paidMarketing)
              }}
            />
            {t('edit_domain_products.paid_marketing_protection')}
          </NoActiveDomainProductsModalOption>
          <NoActiveDomainProductsModalOption>
            <Toggle
              value={botZapping}
              disabled={!packagesData?.botZappingAllowed || !chosenDomain}
              onChange={() => {
                setBotZapping(!botZapping)
              }}
            />
            {t('edit_domain_products.bot_zapping')}
          </NoActiveDomainProductsModalOption>
        </NoActiveDomainProductsModalWrapper>
      </NoActiveDomainProductsModalStyled>
    </DesignedModal>
  )
}
